import { Plus, TriangleAlert, X } from "lucide-react"

import { Boton } from "@/componentes/ui/Boton"
import { Cabecera, Cuerpo, Fila, Tabla, Td, Th } from "@/componentes/ui/Tabla"

import {
  type CompromisoEmpresa,
  type CompromisoTrabajador,
  DECISIONES,
  nota,
  type Pdi,
  type Resultado,
} from "./tipos"

/**
 * La parte 3 del formato: por bloque, el promedio de cada mirada, la brecha, la nota
 * acordada y su peso; abajo, la nota final y su nivel. Con `onAcordada`, la nota acordada
 * se escribe (en la sesión); sin él, se lee.
 */
export function Consolidado({
  r,
  acordadas,
  onAcordada,
}: {
  r: Resultado
  acordadas?: Record<string, number | null>
  onAcordada?: (bloque: string, valor: number | null) => void
}) {
  return (
    <div className="space-y-2">
      <Tabla etiqueta="Consolidado por bloque">
        <Cabecera>
          <Th>Bloque</Th>
          <Th className="text-right">Peso</Th>
          <Th className="text-right">Auto</Th>
          <Th className="text-right">Dirección</Th>
          <Th className="text-right">Brecha</Th>
          <Th className="text-right">Acordada</Th>
          <Th className="text-right">× peso</Th>
        </Cabecera>
        <Cuerpo>
          {r.bloques.map((b) => (
            <Fila key={b.clave}>
              <Td>
                {b.clave}. {b.nombre}
              </Td>
              <Td className="text-right tabular-nums">{Math.round(Number(b.peso) * 100)} %</Td>
              <Td className="text-right tabular-nums">{nota(b.auto)}</Td>
              <Td className="text-right tabular-nums">{nota(b.direccion)}</Td>
              <Td
                className="text-right tabular-nums"
                style={b.revisar ? { color: "var(--warning)" } : undefined}
              >
                {nota(b.brecha)}
                {b.revisar && <span className="sr-only"> (revisar con evidencias)</span>}
              </Td>
              <Td className="text-right tabular-nums">
                {onAcordada ? (
                  <input
                    aria-label={`Nota acordada del bloque ${b.clave}`}
                    className="campo w-20 text-right"
                    type="number"
                    min={1}
                    max={5}
                    step={0.01}
                    placeholder={nota(b.direccion)}
                    value={acordadas?.[b.clave] ?? ""}
                    onChange={(e) =>
                      onAcordada(b.clave, e.target.value === "" ? null : Number(e.target.value))
                    }
                  />
                ) : (
                  nota(b.acordada)
                )}
              </Td>
              <Td className="text-right tabular-nums">{nota(b.ponderada)}</Td>
            </Fila>
          ))}
          <Fila>
            <Td className="font-semibold">Nota final</Td>
            <Td colSpan={5} className="text-right">
              {r.nivel
                ? `${r.nivel} — ${r.consecuencia}`
                : `Faltan los bloques ${r.faltan.join(", ")}`}
            </Td>
            <Td className="text-right font-semibold tabular-nums">{nota(r.final)}</Td>
          </Fila>
        </Cuerpo>
      </Tabla>
      {r.bloques.some((b) => b.revisar) && (
        <p className="flex items-start gap-2 text-xs" style={{ color: "var(--warning)" }}>
          <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden={true} />
          Hay bloques con brecha mayor que 1,0: revisen casos y evidencias de esos bloques (al menos
          15 minutos) antes de fijar la nota acordada.
        </p>
      )}
      {r.tope_etica && (
        <p className="flex items-start gap-2 text-xs" style={{ color: "var(--destructive)" }}>
          <TriangleAlert size={14} className="mt-0.5 shrink-0" aria-hidden={true} />
          Regla de ética: la Dirección calificó 1 o 2 algún criterio del bloque E. La nota final no
          pasa de «Cumple» y se abre de inmediato un plan de acción sobre ese punto.
        </p>
      )}
    </div>
  )
}

/** La parte 5: el Plan de Desarrollo Individual. Sin `onCambiar`, se lee. */
export function PlanDesarrollo({ pdi, onCambiar }: { pdi: Pdi; onCambiar?: (p: Pdi) => void }) {
  const trabajador = pdi.trabajador ?? []
  const empresa = pdi.empresa ?? []
  const lectura = onCambiar === undefined
  const poner = (p: Partial<Pdi>) => onCambiar?.({ ...pdi, ...p })
  const fila = <T,>(lista: T[], i: number, cambio: Partial<T>) =>
    lista.map((x, j) => (j === i ? { ...x, ...cambio } : x))

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h4 className="text-sm font-semibold">A. Compromisos del trabajador</h4>
        {trabajador.length === 0 && lectura && <p className="text-sm">—</p>}
        {trabajador.map((c, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: filas editables sin identidad propia; los campos son controlados, así que el valor sigue a su fila.
          <div key={`t-${i}`} className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_9rem_auto]">
            {(["que", "accion", "medida"] as const).map((k) => (
              <input
                key={k}
                aria-label={
                  { que: "Qué va a mejorar", accion: "Acción concreta", medida: "Cómo se medirá" }[
                    k
                  ]
                }
                placeholder={
                  { que: "Qué va a mejorar", accion: "Acción concreta", medida: "Cómo se medirá" }[
                    k
                  ]
                }
                className="campo"
                readOnly={lectura}
                value={c[k]}
                onChange={(e) =>
                  poner({
                    trabajador: fila<CompromisoTrabajador>(trabajador, i, { [k]: e.target.value }),
                  })
                }
              />
            ))}
            <input
              aria-label="Fecha límite"
              type="date"
              className="campo"
              readOnly={lectura}
              value={c.fecha}
              onChange={(e) =>
                poner({
                  trabajador: fila<CompromisoTrabajador>(trabajador, i, { fecha: e.target.value }),
                })
              }
            />
            {!lectura && (
              <Boton
                tamano="sm"
                aria-label="Quitar compromiso"
                onClick={() => poner({ trabajador: trabajador.filter((_, j) => j !== i) })}
              >
                <X size={14} />
              </Boton>
            )}
          </div>
        ))}
        {!lectura && trabajador.length < 3 && (
          <Boton
            tamano="sm"
            onClick={() =>
              poner({ trabajador: [...trabajador, { que: "", accion: "", medida: "", fecha: "" }] })
            }
          >
            <Plus size={14} /> Compromiso del trabajador
          </Boton>
        )}
      </div>

      <div className="space-y-2">
        <h4 className="text-sm font-semibold">B. Compromisos de la empresa</h4>
        {empresa.length === 0 && lectura && <p className="text-sm">—</p>}
        {empresa.map((c, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: filas editables sin identidad propia; los campos son controlados, así que el valor sigue a su fila.
          <div key={`e-${i}`} className="grid gap-2 sm:grid-cols-[2fr_1fr_9rem_auto]">
            <input
              aria-label="Compromiso"
              placeholder="Capacitación, herramientas, acompañamiento…"
              className="campo"
              readOnly={lectura}
              value={c.compromiso}
              onChange={(e) =>
                poner({
                  empresa: fila<CompromisoEmpresa>(empresa, i, { compromiso: e.target.value }),
                })
              }
            />
            <input
              aria-label="Responsable"
              placeholder="Responsable"
              className="campo"
              readOnly={lectura}
              value={c.responsable}
              onChange={(e) =>
                poner({
                  empresa: fila<CompromisoEmpresa>(empresa, i, { responsable: e.target.value }),
                })
              }
            />
            <input
              aria-label="Fecha"
              type="date"
              className="campo"
              readOnly={lectura}
              value={c.fecha}
              onChange={(e) =>
                poner({ empresa: fila<CompromisoEmpresa>(empresa, i, { fecha: e.target.value }) })
              }
            />
            {!lectura && (
              <Boton
                tamano="sm"
                aria-label="Quitar compromiso"
                onClick={() => poner({ empresa: empresa.filter((_, j) => j !== i) })}
              >
                <X size={14} />
              </Boton>
            )}
          </div>
        ))}
        {!lectura && (
          <Boton
            tamano="sm"
            onClick={() =>
              poner({ empresa: [...empresa, { compromiso: "", responsable: "", fecha: "" }] })
            }
          >
            <Plus size={14} /> Compromiso de la empresa
          </Boton>
        )}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="text-sm">
          C. Decisión de la Dirección
          <select
            className="campo mt-1"
            disabled={lectura}
            value={pdi.decision ?? ""}
            onChange={(e) => poner({ decision: e.target.value })}
          >
            <option value="">—</option>
            {DECISIONES.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          Revisión de seguimiento del PDI
          <input
            type="date"
            className="campo mt-1"
            readOnly={lectura}
            value={pdi.seguimiento ?? ""}
            onChange={(e) => poner({ seguimiento: e.target.value })}
          />
        </label>
      </div>
    </div>
  )
}
